import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DefaultGraphStyle, gaugeMaxDivisions } from '@models/graphs';
import { DeviceFlagsService } from '@service/device-flags.service';
import { AlarmLevel } from 'ops/common/models/alarm-level';
import { Channel, ChannelFns, FlagOption } from 'ops/common/models/channel';
import { ECharts, EChartsOption } from 'echarts';
import { GaugeChart } from 'echarts/charts';
import { LegendComponent, TooltipComponent } from 'echarts/components';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

const gaugeGraphBgColor = new Map();
gaugeGraphBgColor.set(AlarmLevel.Warning, DefaultGraphStyle.alarmWarning);
gaugeGraphBgColor.set(AlarmLevel.Caution, DefaultGraphStyle.alarmCaution);
gaugeGraphBgColor.set(AlarmLevel.Alarm, DefaultGraphStyle.alarm);

const gaugeGraphValueColor = new Map();
gaugeGraphValueColor.set(AlarmLevel.Warning, DefaultGraphStyle.alarmWarningHover);
gaugeGraphValueColor.set(AlarmLevel.Caution, DefaultGraphStyle.alarmCautionHover);
gaugeGraphValueColor.set(AlarmLevel.Alarm, DefaultGraphStyle.alarmHover);

const DEFAULT_CUSTOM = { fontSizeSmall: 14, fontSizeBig: 24 };
const DEFAULT_GAUGE = { fontSizeSmall: 10, fontSizeBig: 18 };

@Component({
    selector: 'app-gauge-graph',
    templateUrl: './gauge-graph.component.html',
    styleUrls: ['./gauge-graph.component.css'],
    standalone: false,
})
export class GaugeGraphComponent implements OnInit, OnDestroy {
    @Input() channel: Channel;
    @Input() divisions: number = 0;
    @Input() isCustom: boolean = false;
    @Input() inCustomEditMode: boolean = false;
    @Output() onRemoveItem: EventEmitter<void> = new EventEmitter();

    xAxisColorArr = [];
    xAxisColorMap = new Map();
    valueColorMap = new Map();
    // graphAlarmLevel;

    readonly echartsExtentions: any[];
    echartsOptions: EChartsOption = {};
    chart: ECharts = null;

    channelValue = undefined;

    flagsSubscription: Subscription;

    statusString: string = '';
    status: FlagOption = FlagOption.UNDEFINED;

    constructor(
        private flagService: DeviceFlagsService,
        private translateService: TranslateService,
    ) {
        this.echartsExtentions = [GaugeChart, TooltipComponent, LegendComponent];
    }

    ngOnInit() {
        this.channelValue = this.flagService.getFlags(this.channel)?.value;
        this.updateStatusString();

        this.initialXAxisColorMap();
        this.initialXAxisColorArr();
        this.initGaugeGraphOption();

        this.flagsSubscription = this.flagService.onAllFlagsUpdated().subscribe(() => {
            this.channelValue = this.flagService.getFlags(this.channel)?.value;
            this.updateStatusString();
            this.updateGaugeGraphOption();
        });
    }

    ngOnDestroy() {
        this.flagsSubscription?.unsubscribe();
    }

    updateStatusString() {
        this.status = this.flagService.getStatus(this.channel);
        const statusString = ChannelFns.getStatusString(this.status);
        if (statusString == '') {
            this.statusString = '';
            return;
        }
        this.statusString = this.translateService.instant(statusString);
    }

    updateGaugeGraphOption() {
        let newseries = { ...this.echartsOptions };
        newseries.series[0].data[0].value = this.channelValue;
        newseries.series[0].detail.rich.status.color = this.valueColorMap.get(this.status);
        newseries.series[0].detail.rich.value.color = this.valueColorMap.get(this.status);
        this.echartsOptions = newseries;
        this.chart.setOption(this.echartsOptions);
    }

    initGaugeGraphOption() {
        this.echartsOptions = {
            series: [
                {
                    type: 'gauge',
                    radius: '100%',
                    detail: {
                        formatter: (value) => {
                            return `{status| ${this.statusString}}\n{value| ${value}}\n{unit| ${this.channel.opsData.measurementUnit}}`;
                        },
                        offsetCenter: [0, '65%'],
                        rich: {
                            status: {
                                lineHeight: 14,
                                fontSize: this.isCustom
                                    ? DEFAULT_CUSTOM.fontSizeSmall
                                    : DEFAULT_GAUGE.fontSizeSmall,
                                color: this.valueColorMap.get(this.status),
                                align: 'center',
                            },
                            value: {
                                lineHeight: 24,
                                fontSize: this.isCustom
                                    ? DEFAULT_CUSTOM.fontSizeBig
                                    : DEFAULT_GAUGE.fontSizeBig,
                                fontWeight: 'bolder',
                                color: this.valueColorMap.get(this.status),
                                align: 'center',
                            },
                            unit: {
                                lineHeight: 14,
                                fontSize: this.isCustom
                                    ? DEFAULT_CUSTOM.fontSizeSmall
                                    : DEFAULT_GAUGE.fontSizeSmall,
                                color: 'rgba(255, 255, 255, 0.7)',
                                align: 'center',
                            },
                        },
                    },

                    pointer: {
                        itemStyle: {
                            color: this.valueColorMap.get(this.status),
                        },
                    },
                    axisLine: {
                        lineStyle: {
                            width: 15,
                            color: this.xAxisColorArr,
                            opacity: this.channelValue ? 1 : 0.4,
                        },
                    },
                    axisTick: {
                        length: 5,
                        lineStyle: {
                            color: 'auto',
                            width: 3,
                            opacity: this.channelValue ? 1 : 0.4,
                        },
                    },
                    axisLabel: {
                        distance: 25,
                        fontSize: 10,
                        color: 'rgba(255, 255, 255, 0.7)',
                        formatter: (value) => {
                            const scale = this.channel.fcuData.scale;
                            const decimalPlaces = scale.toString().length - 1;
                            const result = value / Math.pow(10, decimalPlaces);
                            return result.toFixed(decimalPlaces);
                        },
                    },
                    splitLine: {
                        length: 15,
                        lineStyle: {
                            color: 'auto',
                            width: 3,
                        },
                    },
                    data: [
                        {
                            value: this.channelValue ? this.channelValue : 0,
                        },
                    ],
                    min: this.channel.fcuData.measurableRange.min,
                    max: this.channel.fcuData.measurableRange.max,
                    splitNumber: this.getSplitNumber(),
                },
            ],
        };
    }

    onChartInit(event: ECharts) {
        this.chart = event;
    }

    getSplitNumber() {
        return this.divisions !== 0
            ? Math.min(Math.abs(this.divisions), gaugeMaxDivisions)
            : gaugeMaxDivisions;
    }

    initialXAxisColorArr() {
        this.xAxisColorArr = [];
        let measurableRange = this.channel.fcuData.measurableRange;
        let totalRange = Math.abs(measurableRange.max - measurableRange.min);
        let ranges = this.calculateRanges();
        let rangeKeys = Array.from(ranges.keys());
        let previewRange = 0;

        for (let i = 0; i < rangeKeys.length; i++) {
            let range = ranges.get(rangeKeys[i]);
            let percentage = previewRange + (range.max - range.min) / totalRange;
            if (percentage > 0) {
                this.xAxisColorArr.push([percentage, this.xAxisColorMap.get(rangeKeys[i])]);
            }

            previewRange = previewRange + (range.max - range.min) / totalRange;
        }
    }

    calculateRanges() {
        const fcuAlarms = this.channel.fcuData;
        let rangeMap = new Map();

        const veryHigh = this.toRange(
            fcuAlarms.alarmSettingVeryHigh.setPoint,
            fcuAlarms.measurableRange.max,
            !fcuAlarms.alarmSettingVeryHigh.enable,
        );
        const high = this.toRange(
            fcuAlarms.alarmSettingHigh.setPoint,
            veryHigh.min || fcuAlarms.measurableRange.max,
            !fcuAlarms.alarmSettingHigh.enable,
        );

        const veryLow = this.toRange(
            fcuAlarms.measurableRange.min,
            fcuAlarms.alarmSettingVeryLow.setPoint,
            !fcuAlarms.alarmSettingVeryLow.enable,
        );
        const low = this.toRange(
            veryLow.max || fcuAlarms.measurableRange.min,
            fcuAlarms.alarmSettingLow.setPoint,
            !fcuAlarms.alarmSettingLow.enable,
        );
        const normal = this.toRange(
            low.max || veryLow.max || fcuAlarms.measurableRange.min,
            high.min || veryHigh.min || fcuAlarms.measurableRange.max,
        );

        rangeMap.set(FlagOption.LOW_LOW, veryLow);
        rangeMap.set(FlagOption.LOW, low);
        rangeMap.set(FlagOption.NORMAL, normal);
        rangeMap.set(FlagOption.HIGH, high);
        rangeMap.set(FlagOption.HIGH_HIGH, veryHigh);

        return rangeMap;
    }

    toRange(min: number, max: number, zeroOut: boolean = false): { min: number; max: number } {
        return zeroOut ? { min: 0, max: 0 } : { min, max };
    }

    initialXAxisColorMap() {
        this.xAxisColorMap.set(
            FlagOption.LOW_LOW,
            gaugeGraphBgColor.get(this.channel.fcuData.alarmSettingVeryLow.alarmLevel),
        );
        this.xAxisColorMap.set(
            FlagOption.LOW,
            gaugeGraphBgColor.get(this.channel.fcuData.alarmSettingLow.alarmLevel),
        );
        this.xAxisColorMap.set(
            FlagOption.HIGH,
            gaugeGraphBgColor.get(this.channel.fcuData.alarmSettingHigh.alarmLevel),
        );
        this.xAxisColorMap.set(
            FlagOption.HIGH_HIGH,
            gaugeGraphBgColor.get(this.channel.fcuData.alarmSettingVeryHigh.alarmLevel),
        );
        this.xAxisColorMap.set(FlagOption.NORMAL, DefaultGraphStyle.alarmNormal);

        this.xAxisColorMap.set(FlagOption.UNDEFINED, DefaultGraphStyle.gaugeDefault);
        this.valueColorMap = new Map(this.xAxisColorMap);
        this.valueColorMap.set(FlagOption.UNDEFINED, DefaultGraphStyle.alarmDefault);
        this.valueColorMap.set(FlagOption.UNDEFINED, DefaultGraphStyle.alarmDefault);
        this.valueColorMap.set(FlagOption.UNDEFINED, DefaultGraphStyle.alarmDefault);
    }

    checkChannelValue() {
        return this.channelValue === undefined;
    }

    removeItem() {
        this.onRemoveItem.emit();
    }
}
